import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import PushPinIcon from "@mui/icons-material/PushPin";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import { getSideBarMenuIcon } from "../../../common/data";
import { MenuEntry, useSideMenu, usePinnedMenu } from "./useSideMenu";
import "./sidebar.css";

/** Ranks how well `query` matches `text`: exact word start > substring > letters in order. 0 = no match. */
const score = (text: string, query: string) => {
  const t = text.toLowerCase();
  const q = query.toLowerCase().trim();
  if (!q) return 1;
  if (t.startsWith(q)) return 100;
  if (t.split(/[\s/&-]+/).some((w) => w.startsWith(q))) return 80;
  if (t.includes(q)) return 60;
  let i = 0;
  for (const ch of t) if (ch === q[i]) i++;
  return i === q.length ? 20 : 0;
};

export const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
export const SEARCH_SHORTCUT = isMac ? "⌘K" : "Ctrl K";

/** Jump-to-page search over every menu entry the user can open. */
const CommandPalette = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const navigate = useNavigate();
  const { entries } = useSideMenu();
  const { pinned } = usePinnedMenu();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const results = useMemo(() => {
    const ranked = entries
      .map((e) => ({ e, s: Math.max(score(e.label, query), score(`${e.parent?.name ?? ""} ${e.label}`, query) - 10) + (pinned.includes(e.to) ? 5 : 0) }))
      .filter((r) => r.s > 0);
    if (query.trim()) ranked.sort((a, b) => b.s - a.s);
    return ranked.map((r) => r.e);
  }, [entries, query, pinned]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
    }
  }, [open]);
  useEffect(() => setActive(0), [query]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const go = (entry?: MenuEntry) => {
    if (!entry) return;
    onClose();
    navigate(entry.to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[active]);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: "sb-palette", sx: { borderRadius: "16px", m: 2, alignSelf: "flex-start", mt: "12vh" } }}
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(15,23,42,.35)", backdropFilter: "blur(3px)" } } }}
    >
      <div className="sb-palette-search">
        <SearchIcon />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search pages… e.g. orders, billing, reports"
          aria-label="Search pages"
          role="combobox"
          aria-expanded
          aria-controls="sb-palette-list"
          aria-activedescendant={results[active] ? `sb-opt-${active}` : undefined}
        />
        <kbd className="sb-kbd">Esc</kbd>
      </div>

      <ul className="sb-palette-list" id="sb-palette-list" role="listbox" ref={listRef}>
        {results.length === 0 && <li className="sb-palette-empty">No pages match “{query}”.</li>}
        {results.map((r, i) => {
          const icon = getSideBarMenuIcon(r.item.path, r.item.name) ?? <MoreHorizIcon />;
          return (
            <li
              key={r.to}
              id={`sb-opt-${i}`}
              data-idx={i}
              role="option"
              aria-selected={i === active}
              className={i === active ? "on" : ""}
              onMouseMove={() => setActive(i)}
              onClick={() => go(r)}
            >
              <span className="sb-palette-icon">{icon}</span>
              <span className="sb-palette-text">
                <strong>{r.label}</strong>
                {r.parent && <small>{r.parent.name}</small>}
              </span>
              {pinned.includes(r.to) && <PushPinIcon className="sb-palette-pin" />}
              {i === active && <KeyboardReturnIcon className="sb-palette-enter" />}
            </li>
          );
        })}
      </ul>

      <div className="sb-palette-foot">
        <span><kbd className="sb-kbd">↑</kbd><kbd className="sb-kbd">↓</kbd> to move</span>
        <span><kbd className="sb-kbd">Enter</kbd> to open</span>
        <span><kbd className="sb-kbd">{SEARCH_SHORTCUT}</kbd> anywhere</span>
      </div>
    </Dialog>
  );
};

export default CommandPalette;
